import { BaseIdentifiers } from '../common/identifiers';

export type Position = BaseIdentifiers &
  (ClosedPosition | OpenPosition) & {
    nextPositionId?: string;
    previousPositionId?: string;
    correctionPositionId?: string;
  };

export interface ClosedPosition {
  id: string;
  externalId: string;
  symbol: string;
  openDate: Date;
  closeDate: Date;
  side: string;
  fee: number;
  entry: number;
  exit?: number;
  quantity: number;
  swap?: number;
  comment?: string;
  minTradeDuration?: number;
  profitAndLoss: number;
  status: ClosedPositionStatus;
  accountId: string;
  isClosed: boolean;
  isValid?: boolean;
  invalidReasons?: PositionInvalidReason[];
  isManuallyEdited?: boolean;
  sl?: number;
  tp?: number;
  pips?: number;
  pipValue?: number;
  feeRebate?: FeeRebate;
}

export enum PositionSide {
  Buy = 'Buy',
  Sell = 'Sell',
  Correction = 'Correction',
}

export enum PositionInvalidReason {
  DifferenceBetweenEntryAndExit10Cents = 'difference-between-entry-and-exit-10-cents',
  Minimum60SecondsBetweenOpenAndClose = 'minimum-60-seconds-position',
  NewsTime = 'news-time',
  MinTradeDuration = 'min-trade-duration',
  BugInTradingSystem = 'bug-in-trading-system',
  MisprintFromTheStockExchange = 'misprint-from-the-stock-exchange',
  AfterContestEnd = 'after-contest-end',
  UnsupportedSymbol = 'unsupported-symbol',
}

export const PositionManualEditReason = [
  PositionInvalidReason.MisprintFromTheStockExchange,
  PositionInvalidReason.BugInTradingSystem,
];

export const DEFAULT_TRADE_DURATION = 60;
export const DEFAULT_CONTEST_TRADE_DURATION = 0;

export enum ClosedPositionStatus {
  Win = 'Win',
  Loss = 'Loss',
}

export enum FeeRebateStatus {
  Pending = 'pending',
  Done = 'done',
  Error = 'error',
}

export interface FeeRebate {
  status: FeeRebateStatus;
  rebatedAt?: Date;
}

export interface OpenPosition {
  id: string;
  date: string;
  symbol: string;
  accountId: string;
  side: string;
  swap?: number;
  entry: number;
  exit?: number;
  fee: number;
  profitAndLoss: number;
  quantity: number;
  isClosed?: boolean;
  isValid?: boolean;
  externalId?: string;
  openDate?: Date;
  closeDate?: Date;
}

export interface AdminPosition {
  externalId: string;
  symbol: string;
  openDate: string;
  invalidReasons: PositionInvalidReason[];
  isClosed: boolean;
  isValid: boolean;
  side: PositionSide;
  minTradeDuration: number;
}

export function buildCorrectionPosition(original: Position): Position {
  const oppositeSign = original.isValid ? -1 : 1;
  const correctionExternalId = `${original.externalId}-correction`;
  const pnl = original.profitAndLoss ?? 0;

  const correctionPosition: Partial<Position> = {
    accountId: original.accountId,
    externalId: correctionExternalId,
    symbol: original.symbol,
    openDate: original.openDate,
    closeDate: original.closeDate,
    createdAt: original.createdAt,
    updatedAt: original.updatedAt,
    side: PositionSide.Correction,
    entry: 0,
    exit: 0,
    quantity: 0,
    fee: 0,
    swap: 0,
    profitAndLoss: oppositeSign * pnl,
    isValid: true,
    isClosed: true,
    sl: 0,
    tp: 0,
    pips: 0,
    pipValue: 0,
    invalidReasons: [],
  };

  return correctionPosition as Position;
}
